import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PauseCircleOutlineOutlinedIcon from '@mui/icons-material/PauseCircleOutlineOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import PreviewOutlinedIcon from '@mui/icons-material/PreviewOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import type {
  AiCampaignContentDto,
  AiCampaignCreateRequestDto,
  AiCampaignDraftsResponseDto,
  AiCampaignDto,
  AiCampaignRecipientPreviewResponseDto,
  AiCampaignSegmentDto,
  AiCampaignScheduleDto,
  AiCampaignToneDto,
  AiCampaignUpdateRequestDto,
} from '@/shared/api/ai-campaigns';

import { CampaignRecipientPreview } from './CampaignRecipientPreview';

export type CampaignChannelOption = {
  id: string;
  name?: string;
  protocol?: string;
  status?: string;
};

type SaveDraftInput = {
  campaignId?: string;
  create: AiCampaignCreateRequestDto;
  update: AiCampaignUpdateRequestDto;
};

const TONES: AiCampaignToneDto[] = ['friendly', 'expert', 'concise'];
const VARIANT_COUNT_OPTIONS = [1, 2, 3, 4, 5];

function toInputDate(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toISOString().slice(0, 16);
}

function toIsoDate(value: string) {
  if (!value) return null;
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function defaultSegment(channelIds: string[]): AiCampaignSegmentDto {
  return {
    channel_ids: channelIds,
    exclude_blocked: true,
    require_chat: true,
    limit: 500,
  };
}

function defaultSchedule(): AiCampaignScheduleDto {
  return {
    mode: 'manual',
    start_at: null,
    timezone: 'UTC',
  };
}

export function CampaignEditor({
  companyId,
  campaign,
  channels,
  moduleChannelIds,
  moduleEnabled,
  safeAutomationCampaignEnabled,
  busy = false,
  onSaveDraft,
  onPreviewRecipients,
  onGenerateDrafts,
  onPrepare,
  onStart,
  onPause,
  onCancel,
}: {
  companyId: string;
  campaign: AiCampaignDto | null;
  channels: CampaignChannelOption[];
  moduleChannelIds: string[];
  moduleEnabled: boolean;
  safeAutomationCampaignEnabled: boolean;
  busy?: boolean;
  onSaveDraft: (input: SaveDraftInput) => Promise<AiCampaignDto>;
  onPreviewRecipients: (
    data: Pick<AiCampaignCreateRequestDto, 'company_id'> & {
      channel_ids: string[];
      query?: string;
      in_contact_list?: boolean;
      limit?: number;
    }
  ) => Promise<AiCampaignRecipientPreviewResponseDto>;
  onGenerateDrafts: (data: {
    company_id: string;
    channel_ids: string[];
    goal: string;
    audience: string;
    tone: AiCampaignToneDto;
    variants: number;
    instructions?: string;
  }) => Promise<AiCampaignDraftsResponseDto>;
  onPrepare: (campaignId: string) => Promise<AiCampaignDto>;
  onStart: (campaignId: string) => Promise<AiCampaignDto>;
  onPause: (campaignId: string) => Promise<AiCampaignDto>;
  onCancel: (campaignId: string) => Promise<AiCampaignDto>;
}) {
  const scopedChannelIds = moduleChannelIds.length
    ? moduleChannelIds
    : channels.map((channel) => channel.id);
  const availableChannels = channels.filter((channel) =>
    scopedChannelIds.includes(channel.id)
  );
  const fallbackChannelIds = availableChannels.slice(0, 1).map((item) => item.id);

  const [name, setName] = useState('');
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [inContactList, setInContactList] = useState<'all' | 'yes' | 'no'>(
    'all'
  );
  const [limit, setLimit] = useState(500);
  const [goal, setGoal] = useState('');
  const [audience, setAudience] = useState('');
  const [tone, setTone] = useState<AiCampaignToneDto>('friendly');
  const [variantCount, setVariantCount] = useState(3);
  const [instructions, setInstructions] = useState('');
  const [variants, setVariants] = useState<AiCampaignContentDto['variants']>(
    []
  );
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [scheduleMode, setScheduleMode] =
    useState<AiCampaignScheduleDto['mode']>('manual');
  const [startAt, setStartAt] = useState('');
  const [preview, setPreview] =
    useState<AiCampaignRecipientPreviewResponseDto | null>(null);

  useEffect(() => {
    setName(campaign?.name ?? 'Новая AI-кампания');
    setSelectedChannelIds(campaign?.segment.channel_ids ?? fallbackChannelIds);
    setQuery(campaign?.segment.query ?? '');
    setInContactList(
      campaign?.segment.in_contact_list === true
        ? 'yes'
        : campaign?.segment.in_contact_list === false
          ? 'no'
          : 'all'
    );
    setLimit(campaign?.segment.limit ?? 500);
    setGoal(campaign?.content?.goal ?? '');
    setAudience(campaign?.content?.audience ?? '');
    setTone(campaign?.content?.tone ?? 'friendly');
    setInstructions(campaign?.content?.instructions ?? '');
    setVariants(campaign?.content?.variants ?? []);
    setSelectedVariantId(campaign?.content?.selected_variant_id ?? '');
    setScheduleMode(campaign?.schedule.mode ?? 'manual');
    setStartAt(toInputDate(campaign?.schedule.start_at));
    setPreview(null);
  }, [campaign?.id, channels.length, moduleChannelIds.join('|')]);

  const selectedVariant = variants.find((item) => item.id === selectedVariantId);
  const canPreview = moduleEnabled && selectedChannelIds.length > 0 && !busy;
  const canGenerate =
    moduleEnabled &&
    selectedChannelIds.length > 0 &&
    goal.trim().length >= 3 &&
    audience.trim().length >= 2 &&
    !busy;
  const scheduleValid =
    scheduleMode === 'manual' || toIsoDate(startAt) !== null;
  const canStart =
    moduleEnabled &&
    safeAutomationCampaignEnabled &&
    Boolean(preview?.items.length) &&
    Boolean(selectedVariant) &&
    selectedChannelIds.length > 0 &&
    goal.trim().length >= 3 &&
    audience.trim().length >= 2 &&
    scheduleValid &&
    !busy;
  const canControl =
    campaign?.status === 'scheduled' || campaign?.status === 'running';

  const segment = useMemo<AiCampaignSegmentDto>(
    () => ({
      ...defaultSegment(selectedChannelIds),
      query: query.trim() || undefined,
      in_contact_list:
        inContactList === 'all' ? undefined : inContactList === 'yes',
      limit,
    }),
    [selectedChannelIds, query, inContactList, limit]
  );
  const schedule = useMemo<AiCampaignScheduleDto>(
    () => ({
      ...defaultSchedule(),
      mode: scheduleMode,
      start_at: scheduleMode === 'scheduled' ? toIsoDate(startAt) : null,
    }),
    [scheduleMode, startAt]
  );
  const content = useMemo<AiCampaignContentDto | null>(() => {
    if (!selectedVariant) return null;

    return {
      goal: goal.trim(),
      audience: audience.trim(),
      tone,
      instructions: instructions.trim() || undefined,
      variants,
      selected_variant_id: selectedVariant.id,
    };
  }, [audience, goal, instructions, selectedVariant, tone, variants]);

  const saveDraft = () =>
    onSaveDraft({
      campaignId: campaign?.id,
      create: {
        company_id: companyId,
        name: name.trim() || 'AI-кампания',
        segment,
        schedule,
      },
      update: {
        name: name.trim() || 'AI-кампания',
        segment,
        schedule,
        ...(content ? { content } : {}),
      },
    });

  const handlePreview = async () => {
    if (!canPreview) return;
    const result = await onPreviewRecipients({
      company_id: companyId,
      channel_ids: selectedChannelIds,
      query: query.trim() || undefined,
      in_contact_list:
        inContactList === 'all' ? undefined : inContactList === 'yes',
      limit,
    });
    setPreview(result);
  };

  const handleGenerate = async () => {
    if (!canGenerate) return;
    const result = await onGenerateDrafts({
      company_id: companyId,
      channel_ids: selectedChannelIds,
      goal: goal.trim(),
      audience: audience.trim(),
      tone,
      variants: variantCount,
      instructions: instructions.trim() || undefined,
    });
    setVariants(result.variants);
    setSelectedVariantId(result.variants[0]?.id ?? '');
  };

  const handleStart = async () => {
    if (!canStart || !content) return;
    const saved = await saveDraft();
    await onPrepare(saved.id);
    await onStart(saved.id);
  };

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6">
                {campaign ? 'Редактирование кампании' : 'Новая кампания'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Сегмент, текст, расписание и ручной запуск через Safe Automation
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {campaign ? (
                <Chip size="small" label={campaign.status} sx={{ borderRadius: 1 }} />
              ) : null}
              <Button
                size="small"
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                disabled={busy || selectedChannelIds.length === 0}
                onClick={() => void saveDraft()}
              >
                Сохранить черновик
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrowOutlinedIcon />}
                disabled={!canStart}
                onClick={() => void handleStart()}
              >
                Подготовить и запустить
              </Button>
              {canControl ? (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PauseCircleOutlineOutlinedIcon />}
                    disabled={busy}
                    onClick={() => campaign && void onPause(campaign.id)}
                  >
                    Пауза
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    startIcon={<CancelOutlinedIcon />}
                    disabled={busy}
                    onClick={() => campaign && void onCancel(campaign.id)}
                  >
                    Отменить
                  </Button>
                </>
              ) : null}
            </Stack>
          </Stack>

          {!moduleEnabled ? (
            <Alert severity="warning">
              Модуль AI-кампаний выключен для этого профиля.
            </Alert>
          ) : null}
          {!safeAutomationCampaignEnabled ? (
            <Alert severity="warning">
              Источник campaign не включен в Safe Automation, запуск недоступен.
            </Alert>
          ) : null}

          <TextField
            label="Название"
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="subtitle2">Сегмент</Typography>
              <TextField
                select
                size="small"
                SelectProps={{ multiple: true }}
                label="Каналы"
                value={selectedChannelIds}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedChannelIds(
                    typeof value === 'string' ? value.split(',') : value
                  );
                  setPreview(null);
                }}
              >
                {availableChannels.map((channel) => (
                  <MenuItem key={channel.id} value={channel.id}>
                    {channel.name || channel.id}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Поиск по получателям"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setPreview(null);
                }}
              />
              <TextField
                select
                size="small"
                label="Список контактов"
                value={inContactList}
                onChange={(event) => {
                  setInContactList(event.target.value as 'all' | 'yes' | 'no');
                  setPreview(null);
                }}
              >
                <MenuItem value="all">Все</MenuItem>
                <MenuItem value="yes">Только в списке</MenuItem>
                <MenuItem value="no">Только вне списка</MenuItem>
              </TextField>
              <TextField
                size="small"
                type="number"
                label="Лимит"
                value={limit}
                inputProps={{ min: 1, max: 10000 }}
                onChange={(event) => {
                  setLimit(Number(event.target.value) || 1);
                  setPreview(null);
                }}
              />
              <Button
                variant="outlined"
                startIcon={<PreviewOutlinedIcon />}
                disabled={!canPreview}
                onClick={() => void handlePreview()}
              >
                Preview получателей
              </Button>
            </Stack>

            <Stack spacing={1.25}>
              <Typography variant="subtitle2">Контент</Typography>
              <TextField
                size="small"
                multiline
                minRows={2}
                label="Цель"
                value={goal}
                onChange={(event) => setGoal(event.target.value)}
              />
              <TextField
                size="small"
                multiline
                minRows={2}
                label="Аудитория"
                value={audience}
                onChange={(event) => setAudience(event.target.value)}
              />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 120px' },
                  gap: 1,
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Тон"
                  value={tone}
                  onChange={(event) =>
                    setTone(event.target.value as AiCampaignToneDto)
                  }
                >
                  {TONES.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Варианты"
                  value={variantCount}
                  onChange={(event) => setVariantCount(Number(event.target.value))}
                >
                  {VARIANT_COUNT_OPTIONS.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
              <TextField
                size="small"
                multiline
                minRows={2}
                label="Ограничения"
                value={instructions}
                onChange={(event) => setInstructions(event.target.value)}
              />
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeOutlinedIcon />}
                disabled={!canGenerate}
                onClick={() => void handleGenerate()}
              >
                Сгенерировать варианты
              </Button>
            </Stack>
          </Box>

          <Divider />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
              gap: 2,
            }}
          >
            <Stack spacing={1.25}>
              <Typography variant="subtitle2">Варианты текста</Typography>
              {variants.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Сгенерируйте AI-варианты или сохраните кампанию с выбранным
                  текстом позже.
                </Typography>
              ) : (
                variants.map((variant, index) => (
                  <Paper
                    key={variant.id}
                    variant="outlined"
                    sx={{ p: 1.25, borderRadius: 1 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={variant.id === selectedVariantId}
                          onChange={() => setSelectedVariantId(variant.id)}
                        />
                      }
                      label={`Вариант ${index + 1}`}
                    />
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      value={variant.text}
                      onChange={(event) =>
                        setVariants((current) =>
                          current.map((item) =>
                            item.id === variant.id
                              ? { ...item, text: event.target.value }
                              : item
                          )
                        )
                      }
                    />
                  </Paper>
                ))
              )}
            </Stack>

            <Stack spacing={1.25}>
              <Typography variant="subtitle2">Расписание и проверка</Typography>
              <TextField
                select
                size="small"
                label="Режим"
                value={scheduleMode}
                onChange={(event) =>
                  setScheduleMode(event.target.value as AiCampaignScheduleDto['mode'])
                }
              >
                <MenuItem value="manual">Ручной запуск</MenuItem>
                <MenuItem value="scheduled">По расписанию</MenuItem>
              </TextField>
              {scheduleMode === 'scheduled' ? (
                <TextField
                  size="small"
                  type="datetime-local"
                  label="Начать после"
                  value={startAt}
                  onChange={(event) => setStartAt(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={!scheduleValid}
                />
              ) : null}
              <Alert severity={canStart ? 'success' : 'info'}>
                {canStart
                  ? 'Кампания готова к prepare/start.'
                  : 'Для запуска нужны preview с получателями и выбранный вариант текста.'}
              </Alert>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
        <Stack spacing={1}>
          <Typography variant="subtitle2">
            Preview получателей {preview ? `(${preview.items.length})` : ''}
          </Typography>
          <CampaignRecipientPreview preview={preview} />
        </Stack>
      </Paper>
    </Stack>
  );
}
